/**
 * Local walkthrough data so the S22 Ultra UI can be reviewed without Azure AD.
 * Preview never writes to the API.
 */

export const DEMO_PROJECTS = [
  { id: 'demo-71940', job_number: '71940', job_name: 'Pratt Paper — Field walk' },
  { id: 'demo-61075', job_number: '61075', job_name: 'Ottawa Hospital — Mechanical' },
];

export const DEMO_ESTIMATES = [
  { id: 'demo-est-1001', estimate_number: 'E-1001', estimate_name: 'Pratt Paper — bid' },
  { id: 'demo-est-884', estimate_number: 'E-884', estimate_name: 'Hospital mechanical bid' },
];

const pipingRows = [
  {
    id: 101,
    isoSheetNumber: 'ISO-P-014',
    fluidCode: 'PW',
    size1: '6"',
    material1: 'CS',
    pAndId: 'PID-201',
    totalShopCompletion: 65,
    received: 100,
    transportToInstallationArea: 100,
    erectSupportsHangers: 50,
    fitUp: 0,
    boltUpWeld: 0,
    lineCleanupHydroTest: 0,
    heatTracing: 0,
    insulation: 0,
    totalFieldCompletion: 32,
    comments: '',
  },
  {
    id: 102,
    isoSheetNumber: 'ISO-P-022',
    fluidCode: 'IA',
    size1: '2"',
    material1: 'SS',
    pAndId: 'PID-118',
    totalShopCompletion: 40,
    received: 100,
    transportToInstallationArea: 25,
    erectSupportsHangers: 0,
    fitUp: 0,
    boltUpWeld: 0,
    lineCleanupHydroTest: 0,
    heatTracing: 0,
    insulation: 0,
    totalFieldCompletion: 8,
    onHold: true,
    comments: 'Wait on embed plates',
  },
  {
    id: 103,
    isoSheetNumber: 'ISO-P-031',
    fluidCode: 'PW',
    size1: '8"',
    material1: 'CS',
    totalShopCompletion: 100,
    received: 100,
    transportToInstallationArea: 100,
    erectSupportsHangers: 100,
    fitUp: 100,
    boltUpWeld: 100,
    lineCleanupHydroTest: 100,
    heatTracing: 100,
    insulation: 100,
    totalFieldCompletion: 100,
  },
];

const mechanicalRows = [
  {
    id: 201,
    equipmentTagOrCode: 'P-401A',
    description: 'Boiler feed pump A',
    area: 'Bldg 2',
    subArea: 'Pump room',
    equipmentReceiptPercent: 100,
    measureAnchorSolePlate: 100,
    roughSet: 50,
    preAlignment: 0,
    alignAndLevel: 0,
    groutStage: 0,
    punchlistCommissioning: 0,
    totalFieldCompletion: 30,
  },
  {
    id: 202,
    equipmentTagOrCode: 'P-401B',
    description: 'Boiler feed pump B',
    area: 'Bldg 2',
    subArea: 'Pump room',
    equipmentReceiptPercent: 100,
    measureAnchorSolePlate: 0,
    roughSet: 0,
    preAlignment: 0,
    alignAndLevel: 0,
    groutStage: 0,
    punchlistCommissioning: 0,
    totalFieldCompletion: 0,
  },
];

const electricalRows = [
  {
    id: 301,
    equipmentTagOrCode: 'MCC-2A',
    description: 'Process MCC lineup',
    area: 'Electrical',
    conduitInstall: 75,
    cablePull: 25,
    termination: 0,
    testing: 0,
    energize: 0,
    totalFieldCompletion: 20,
  },
];

const generic = (id, tag, description, area, steps) => ({
  id,
  equipmentTagOrCode: tag,
  description,
  area,
  ...steps,
});

export const getDemoRows = (projectId, disciplineId) => {
  if (disciplineId === 'piping') return pipingRows.map((row) => ({ ...row }));
  if (disciplineId === 'mechanical') return mechanicalRows.map((row) => ({ ...row }));
  if (disciplineId === 'electrical') return electricalRows.map((row) => ({ ...row }));
  if (disciplineId === 'structural') {
    return [generic(401, 'ST-12', 'Pipe rack bent 12', 'Yard', {
      receiveShakeOut: 100, layoutDrill: 50, erect: 0, boltUp: 0, paintTouchUp: 0, totalFieldCompletion: 30,
    })];
  }
  if (disciplineId === 'hvac') {
    return [generic(501, 'AHU-3', 'Air handler 3', 'Roof', {
      deliveryToSite: 100, roughInstall: 25, supports: 0, accessories: 0, airBalancing: 0, totalFieldCompletion: 25,
    })];
  }
  if (disciplineId === 'ducts') {
    return [generic(601, 'DT-18', 'Supply main 18', 'Level 2', {
      deliveryToSite: 100, roughSet: 75, supports: 25, bolting: 0, accessories: 0, grout: 0, totalFieldCompletion: 33,
    })];
  }
  if (disciplineId === 'boilermaker') {
    return [generic(701, 'TK-02', 'Field tank shell', 'Tank farm', {
      deliveryToSite: 100, roughSet: 50, supports: 0, bolting: 0, accessories: 0, grout: 0, totalFieldCompletion: 25,
    })];
  }
  if (disciplineId === 'civil') {
    return [generic(801, 'FD-04', 'Pump pad 4', 'Bldg 2', {
      sitePrep: 100, excavation: 100, formwork: 50, rebar: 0, pourConcrete: 0, backfill: 0, totalFieldCompletion: 42,
    })];
  }
  if (disciplineId === 'vessels') {
    return [generic(901, 'V-12', 'Knock-out drum', 'Tank farm', {
      equipmentReceiptPercent: 100, measureAnchorBushHammer: 50, roughSet: 0, grout: 0, vesselCloseOut: 0, insulation: 0, totalFieldCompletion: 25,
    })];
  }
  if (disciplineId === 'chutes') {
    return [generic(1001, 'CH-07', 'Transfer chute 7', 'Yard', {
      receive: 100, installSupports: 50, fieldTrim: 0, weldBoltUp: 0, punchList: 0, totalFieldCompletion: 30,
    })];
  }
  if (disciplineId === 'conveyors') {
    return [generic(1101, 'CV-03', 'Belt conveyor 3', 'Yard', {
      equipmentReceiptPercent: 100, layoutAnchor: 25, prepareConcreteBase: 0, roughSet: 0, alignAndLevel: 0, torqueBolts: 0, groutStage: 0, punchlistCommissioning: 0, totalFieldCompletion: 16,
    })];
  }
  if (disciplineId === 'skids') {
    return [generic(1201, 'SK-08', 'Lube skid', 'Bldg 2', {
      received: 100, layout: 50, grout: 0, roughSet: 0, finalSet: 0, totalFieldCompletion: 30,
    })];
  }
  return [];
};

export const getDemoProjects = () => DEMO_PROJECTS.map((project) => ({ ...project }));
